import React, { useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCamera, faChartColumn, faRepeat, faFaceSmile, faExpand, faPen,
    faMessage, faFilter, faEye, faHeart, faSparkles,
    faCheck, faXmark, faUsers, faVideo, faChartSimple, faChevronDown, faChevronRight, faBadgeCheck, faMoon, faBell,
    faGhost, faStar, faStarSharp, faLock, faLockOpen, faArrowsRotate, faCreditCard, faImage, faPlay,
} from '@fortawesome/pro-solid-svg-icons';
import './OffersModal.scss';
import Button, { BUTTON_VARIANTS } from '../UI/Button/Button';
import createLink from '../../../libs/createLink';
import useCurrentUser from '../../../hooks/useCurrentUser';
import { isCertifEligibleGender } from '../../../const/certification';
import { startTrial, fetchPlansOffers } from '../../../api/public-voissa/plans';
import { USER_CURRENT_KEY } from '../../../api/public-voissa/users';
import {
    OFFERS, MAX_NAME, PLUS_NAME, CONTEXT_COPY, TRIGGER_COPY, PLUS_VITRINE, PLUS_VITRINE_EXTRA, VITRINE_CAP_ALIAS,
    PLUS_CARD_FEATURES, PLUS_FEATURES_IN_PREMIUM, MAX_CARD_FEATURES, plusMoreFeatures,
    CERTIF_PANEL, CERTIF_EYEBROW, CERTIF_SUBHEADLINE, CERTIF_NOTE, CERTIF_HEADLINE, CERTIF_HEADLINE_DEFAULT,
    PLUS_BLOCK_COPY, PLUS_BLOCK_FALLBACK, premiumBlockLines, PLUS_BLOCK_EXCLUSIONS, plusOtherArguments, BLOCK_REASSURANCE,
    MAX_BLOCK_COPY, MAX_BLOCK_FALLBACK, maxBlockLines, OFFERS_REASSURANCE, MAX_SHORT,
    MAX_SHOWCASE, MAX_SHOWCASE_SAMPLE,
} from './offersContent';

// Modale d'offres CENTRALISÉE — remplace ContentLocked / TrialModal / StatsPremiumGate.
// Reproduit les 5 gabarits validés (vitrine de features, hero Max, panneau « Inclus », comparatif).
export const OFFERS_MODAL_CONTEXT = {
    LAUNCH: 'launch',
    BLOCKED: 'blocked',
    TRIAL_END: 'trial-end',
};

// Écrin commun à toutes les ouvertures de la modale (validé le 01/10) : drawer `small` (400 px en
// mode modale, la largeur des maquettes) pour que chaque gabarit ait la même taille, et sans la
// croix du drawer — la modale porte la sienne.
export const OFFERS_DRAWER_OPTIONS = { small: true, showCloseBtn: false };

const TRIAL_PLUS_SLUG = 'voissa-plus';

const ICONS = {
    camera: faCamera, chart: faChartColumn, repeat: faRepeat, smile: faFaceSmile, expand: faExpand, pen: faPen,
    message: faMessage, filter: faFilter, eye: faEye, heart: faHeart, sparkles: faSparkles,
    check: faCheck, users: faUsers, video: faVideo, stats: faChartSimple, badge: faBadgeCheck, moon: faMoon, bell: faBell,
    incognito: faGhost, xmark: faXmark, star: faStar, starSharp: faStarSharp, lock: faLock, lockOpen: faLockOpen, arrowsRotate: faArrowsRotate, creditCard: faCreditCard, image: faImage, play: faPlay, chevronDown: faChevronDown, chevronRight: faChevronRight,
};

// Prix réels via GET /plans/offers (source de vérité vMessenger, réductions user incluses ;
// jamais hardcodés). On annonce le TARIF MENSUEL LE PLUS BAS de la grille — celui de la durée
// la plus longue, la grille étant dégressive — et non le ticket d'entrée du mois seul.
//
// C'est le choix de la maquette (13,32 € et 3,32 €, soit les offres six mois) et celui de
// l'équipe : le « à partir de » doit donner envie, donc porter le montant le plus faible. Le
// `slug` suit le même mouvement et cible cette offre-là au paiement, sans quoi on annoncerait un
// prix pour en facturer un autre. La durée correspondante est renvoyée afin que l'écran puisse
// la mentionner : un tarif mensuel qui suppose six mois d'engagement ne s'affiche pas seul.
// Renvoie { plus, max } en euros (ou null si vMessenger indisponible).
// Exporté : la publicité du fil (Feed/FeedPremiumAd) affiche les mêmes tarifs. Les dupliquer
// aurait fait diverger les deux écrans dès la première évolution de la grille.
export const usePlanPrices = () => {
    const { data } = useQuery(['plans-offers'], () => fetchPlansOffers(), {
        staleTime: 1000 * 60 * 30,
        select: (res) => {
            // Défaut fail-open : si l'API ne répond pas, on ne verrouille pas Voissa Plus.
            if (res?.status !== 'success') return { plus: null, max: null, plusOffer: null, maxOffer: null };
            // Le prix MENSUEL le plus bas de la grille. `pricePerMonth` est fourni par l'API ;
            // à défaut on retombe sur le total, qui vaut le mensuel pour une offre d'un mois.
            // Décision : PAS de sélecteur de durée dans la modale — le choix 1/3/6 mois se fait
            // sur la plateforme de paiement, mais le slug retenu y arrive déjà positionné.
            const perMonth = (o) => (typeof o?.pricePerMonth === 'number' ? o.pricePerMonth : o?.price);
            const cheapestPerMonth = (group) => {
                const offers = (group?.offers || [])
                    .filter((o) => typeof perMonth(o) === 'number' && perMonth(o) > 0);
                if (!offers.length) return null;
                return offers.reduce((a, b) => (perMonth(b) < perMonth(a) ? b : a));
            };
            const plusEntry = cheapestPerMonth(res.plus);
            const maxEntry = cheapestPerMonth(res.premium);
            return {
                plus: plusEntry ? perMonth(plusEntry) : null,
                max: maxEntry ? perMonth(maxEntry) : null,
                plusOffer: plusEntry?.slug || null,
                maxOffer: maxEntry?.slug || null,
                // Durée, total et remise de l'offre retenue : un tarif mensuel qui suppose six
                // mois d'engagement ne s'annonce pas seul. `name` est déjà lisible (« 6 mois »).
                plusDuree: plusEntry?.name || null,
                maxDuree: maxEntry?.name || null,
                plusTotal: typeof plusEntry?.price === 'number' ? plusEntry.price : null,
                maxTotal: typeof maxEntry?.price === 'number' ? maxEntry.price : null,
                plusRemise: plusEntry?.discountPercent || null,
            };
        },
    });
    return data || { plus: null, max: null, plusOffer: null, maxOffer: null };
};

// On n'affiche jamais 0,00 € : prix <= 0 (ou invalide) => null (rien affiché).
// Lignes de l'onglet Voissa Plus visibles avant le dépliant : autant que l'onglet Premium.
const PLUS_TAB_VISIBLE = 5;

const fmtPrice = (v) => (typeof v === 'number' && v > 0 ? `${v.toFixed(2).replace('.', ',')} €` : null);

const Icon = ({ name, className }) => (ICONS[name] ? <FontAwesomeIcon icon={ICONS[name]} className={className} /> : null);

// Titre : segments d'index impair en dégradé. Le pseudo est fusionné dans le 1er segment (parité).
const GradientHeadline = ({ headline, username }) => {
    let parts = Array.isArray(headline) ? headline : [headline];
    if (username && parts.length) parts = [`${username}, ${parts[0]}`, ...parts.slice(1)];
    return (
        <div className="om-title">
            {parts.map((p, i) => (i % 2 === 1 ? <span key={i} className="om-grad">{p}</span> : <React.Fragment key={i}>{p}</React.Fragment>))}
        </div>
    );
};

/**
 * Suffixe d'une ligne de prix.
 *
 * On annonce le tarif MENSUEL le plus bas, qui s'obtient avec la durée la plus longue. L'écrire
 * sans la durée reviendrait à laisser croire qu'un mois coûte ce prix-là : la mention fait donc
 * partie du prix, pas d'une note de bas de page. Elle disparaît d'elle-même si l'offre retenue
 * est déjà mensuelle, auquel cas il n'y a rien à préciser.
 */
const suffixeDuree = (duree, { ensuite = false } = {}) => {
    const base = ensuite ? ' /mois ensuite' : ' /mois';
    if (!duree || /^1\s*mois$/i.test(duree)) return base;
    return `${base} pendant ${duree}`;
};

const PriceLine = ({ value, suffix = ' /mois', align = 'left', prefix = null }) => {
    const p = fmtPrice(value);
    if (!p) return null;
    return (
        <div className={classNames('om-price', `om-price--${align}`)}>
            {prefix && <span className="om-price-prefix">{prefix} </span>}
            <span className="om-price-value">{p}</span><span className="om-price-suffix">{suffix}</span>
        </div>
    );
};

const OffersModal = ({ context = OFFERS_MODAL_CONTEXT.LAUNCH, triggerAction = null, username, canBePriviliged = false, isCertified = false, inDrawer = false, fullWidth = false, initialTab = 'max', handleCancel = () => {} }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const currentUser = useCurrentUser();
    const prices = usePlanPrices();
    const [submitting, setSubmitting] = useState(false);
    // Motif d'échec de l'activation d'essai, affiché sous le CTA (cf. doStartTrial).
    const [trialError, setTrialError] = useState(null);
    // Bascule interne vers le COMPARATIF d'offres (gabarit compact du lancement) : déclenchée par
    // la ligne Voissa Plus du gabarit bloqué Max — on présente l'offre au lieu d'agir directement.
    // Vue « comparatif » : plus rien ne l'ouvre depuis le retrait des liens « Découvrir toute
    // l'offre » (07/10). Figée à false ; les branches qui en dépendent sont à nettoyer à part.
    const showOffers = false;
    const [showTabAll, setShowTabAll] = useState(false);
    // Fenêtre d'exclusivité (compact) : Premium est la carte déployée, mais le contenu de Voissa
    // Plus reste CONSULTABLE — le clic sur sa ligne déploie sa carte teaser (achat indisponible).
    // Gabarit de blocage Premium : la liste complète des avantages est repliée par défaut, comme
    // dans la maquette — la fonction bloquée doit rester le premier regard.
    const [showMaxDetail, setShowMaxDetail] = useState(false);
    // Dépliant « Voir tous les avantages Voissa Plus » du bloc Plus (modales Voissa Plus).
    const [showPlusDetail, setShowPlusDetail] = useState(false);
    // Onglet de la page de choix d'offre. Premium par défaut : c'est l'offre mise en avant.
    // `initialTab: 'plus'` ouvre sur Voissa Plus, quand le membre a cliqué sur ce nom-là
    // (ex. le texte du lien en bio dans les paramètres du profil).
    const [offerTab, setOfferTab] = useState(initialTab === 'plus' ? 'plus' : 'max');

    const canStartTrial = !!currentUser?.subscription?.canStartTrial;

    const name = username || currentUser?.username;

    const trigger = context === OFFERS_MODAL_CONTEXT.BLOCKED ? (TRIGGER_COPY[triggerAction] || null) : null;
    // Vue comparatif (clic sur la ligne Voissa Plus) : tête du lancement, pas celle du blocage.
    const baseHead = showOffers ? CONTEXT_COPY.launch : (trigger || CONTEXT_COPY[context] || CONTEXT_COPY.launch);
    const tier = trigger?.tier || null;

    // Alternative CERTIFICATION (gratuite, profils femme/couple) : proposée uniquement quand la
    // certif accorde réellement la capability bloquée (`certifiable`). `canBePriviliged` vient de
    // useRestriction quand la modale est ouverte par lui ; sinon fallback sur le genre du profil.
    // Calculée AVANT `isBlockedPlus`, qui s'appuie dessus.
    const certifEligible = !!trigger?.certifiable
        && (canBePriviliged || isCertifEligibleGender(currentUser?.profile?.gender));

    // Toute restriction Plus (tier ≠ max, y compris trigger inconnu) présente le COMPARATIF des
    // deux offres : une action bloquée doit montrer les offres et leurs avantages, pas un CTA sec.
    //
    // SAUF pour un profil éligible à la certification : elle lève la limite GRATUITEMENT, c'est
    // donc elle qu'il faut vendre en premier. Sans cette exclusion, le comparatif — testé avant le
    // gabarit certification — l'emportait toujours sur les triggers de palier Plus, et une femme
    // non certifiée se voyait proposer un abonnement avec la certif reléguée à un simple bandeau.
    const isBlockedPlus = context === OFFERS_MODAL_CONTEXT.BLOCKED && tier !== 'max' && !certifEligible;

    const head = baseHead;

    const certified = isCertified || !!currentUser?.isCertified;

    // Redirige vers /paiement en transmettant le palier visé via `plan` et, optionnellement,
    // l'offre sélectionnée via `offer` (slug vMessenger) — récupérés côté paiement pour cibler
    // l'offre. Voissa Max = palier premium par défaut → `plan` laissé vide. `offerSlug` absent =>
    // non transmis (vMessenger retombe sur son offre par défaut, 1 mois).
    const goSubscribe = (slug, offerSlug = null) => {
        handleCancel?.();
        const plan = slug === OFFERS.max.slug ? '' : slug;
        const params = new URLSearchParams();
        if (plan) params.set('plan', plan);
        if (offerSlug) params.set('offer', offerSlug);
        const qs = params.toString();
        navigate(qs ? `${createLink('subscription')}?${qs}` : createLink('subscription'));
    };
    const goCertify = () => { handleCancel?.(); navigate(createLink('userMeSettingsCertification', currentUser)); };
    // `POST /users/me/trial` refuse pour 8 motifs distincts (plan déjà actif, campagne fermée,
    // rôle, ancienneté du compte, activité récente, seuil de suiveurs, audience, essai déjà
    // consommé) — dont plusieurs que le front ne peut PAS pré-calculer : `canStartTrial` est
    // résolu au dernier /users/me et dépend d'un comptage de suiveurs en base. Fermer la modale
    // sans un mot laissait le membre croire que son essai avait démarré. On garde donc la modale
    // ouverte et on affiche le motif du serveur, écrit pour le membre.
    const TRIAL_ERROR_FALLBACK = "Impossible d'activer l'essai pour le moment. Réessayez dans quelques instants.";

    const doStartTrial = async () => {
        if (submitting) return;
        setSubmitting(true);
        setTrialError(null);
        try {
            await startTrial(TRIAL_PLUS_SLUG);
            await queryClient.invalidateQueries([USER_CURRENT_KEY]);
            setSubmitting(false);
            handleCancel?.();
        } catch (e) {
            // On rafraîchit quand même : si le refus vient d'un plan déjà actif, la modale doit
            // refléter le vrai état (canStartTrial repasse à false, la carte bascule sur l'abo).
            await queryClient.invalidateQueries([USER_CURRENT_KEY]);
            // Seuls les refus métier (403 / 409) portent un message rédigé pour le membre ;
            // le reste (réseau, 500, timeout) ne doit jamais être affiché tel quel.
            const isBusinessRefusal = [403, 409].includes(e?.statusCode) && !!e?.message;
            setTrialError(isBusinessRefusal ? e.message : TRIAL_ERROR_FALLBACK);
            setSubmitting(false);
        }
    };

    // Message d'échec sous le CTA d'essai (les deux gabarits portent le même bouton).
    const TrialError = () => (trialError ? <div className="om-card-error" role="alert">{trialError}</div> : null);

    const closeBtn = inDrawer ? (
        <button type="button" className="om-close" aria-label="Fermer" onClick={handleCancel}><FontAwesomeIcon icon={faXmark} /></button>
    ) : null;

    // Avec `greeting`, le pseudo passe dans le sur-titre (« Bonjour Marie ») et quitte le titre.
    const greeting = head.greeting && name ? head.greeting(name) : null;
    const Head = ({ heroIcon, compact = false }) => (
        <div className={classNames('om-head', { 'om-head--compact': compact })}>
            {heroIcon && <div className="om-hero"><Icon name={heroIcon} /></div>}
            {greeting && <div className="om-eyebrow om-eyebrow--greeting">{greeting}</div>}
            {!greeting && head.eyebrow && <div className="om-eyebrow">{head.eyebrow}</div>}
            <GradientHeadline headline={head.headline} username={!greeting && !head.anonymous && (showOffers || context !== OFFERS_MODAL_CONTEXT.BLOCKED) ? name : undefined} />
            {head.subheadline && <div className="om-sub">{head.subheadline}</div>}
        </div>
    );

    // Pied sans lien « Comparer » (la modale EST déjà le comparateur d'offres) : une note
    // éventuelle à gauche, « Plus tard » en drawer — centré quand il est seul.
    const Footer = ({ note = null }) => ((note || inDrawer) ? (
        <div className={classNames('om-foot', { 'om-foot--solo': !note })}>
            {note && <span className="om-foot-note">{note}</span>}
            {inDrawer && <button type="button" className="om-dismiss" onClick={handleCancel}>Plus tard</button>}
        </div>
    ) : null);

    // Gabarit « Premium d'abord » : le clic sur « Découvrir toute l'offre » (showOffers) rend
    // la main au comparatif, qui a sa propre mise en page.
    const blockedPlusLayout = isBlockedPlus && !showOffers;

    let body;

    // 1a-bis) Gabarit BLOCAGE VOISSA PLUS — « Premium d'abord » (maquettes 09/2026).
    // Renversement de présentation : une action réservée à Voissa Plus met désormais Voissa
    // Premium en tête (badge « Recommandé », CTA plein), et Voissa Plus juste en dessous avec ce
    // qu'il ne contient PAS. Premium incluant tout Plus, l'action tentée reste débloquée dans les
    // deux cas — c'est pourquoi la fonctionnalité demandée ouvre les DEUX listes : le membre voit
    // que la formule simple lui suffit, et ce que l'autre lui apporterait en plus.
    //
    // L'essai gratuit reste attaché à Voissa Plus (c'est le seul plan que `POST /users/me/trial`
    // ouvre) : il garde donc sa place sur cette carte, en CTA secondaire.
    //
    // Un même gabarit pour mobile et bureau : la maquette est une colonne, la largeur du drawer
    // suffit à l'accueillir sans variante.
    if (blockedPlusLayout) {
        const copy = PLUS_BLOCK_COPY[triggerAction] || PLUS_BLOCK_FALLBACK;
        // Icône de tête : celle de la tuile de vitrine correspondante, via le même alias que la
        // vitrine (la lecture d'une story met en avant la tuile « Stories »). Pas d'emoji,
        // contrairement à la maquette : le reste du site est en FontAwesome.
        const capKey = VITRINE_CAP_ALIAS[triggerAction] || triggerAction;
        const tile = [...PLUS_VITRINE, ...Object.values(PLUS_VITRINE_EXTRA)].find((t) => t.cap === capKey);
        const OfferLine = ({ label, included = true, strong = false }) => (
            <li className={classNames('om-bp-li', { 'om-bp-li--no': !included, 'om-bp-li--key': strong })}>
                <Icon name={included ? 'check' : 'xmark'} className="om-bp-mark" />
                <span>{label}</span>
            </li>
        );

        body = (
            <>
                <div className="om-bp-head">
                    <div className="om-bp-kicker">{copy.kicker}</div>
                    {/* Le pictogramme vit DANS le titre, comme dans la maquette : posé au-dessus en
                        pastille, il prenait la moitié de la tête et volait la vedette au message. */}
                    <div className="om-bp-title">
                        {tile && <Icon name={tile.icon} className="om-bp-title-icon" />}
                        {copy.title}
                    </div>
                </div>

                <div className="om-bp-offers">
                    <section className="om-bp-offer om-bp-offer--reco">
                        <div className="om-bp-orow">
                            <span className="om-bp-logo" aria-hidden="true"><i /></span>
                            <span className="om-bp-otext">
                                <span className="om-bp-oname">{OFFERS.max.name}</span>
                                <span className="om-bp-oline">Tout Voissa, sans limites.</span>
                            </span>
                            <span className="om-bp-reco"><Icon name="starSharp" />Recommandé</span>
                        </div>
                        <ul className="om-bp-list">
                            <OfferLine label={copy.featureMax || copy.feature} strong />
                            {premiumBlockLines(copy.featureMax || copy.feature).map((l) => <OfferLine key={l} label={l} />)}
                        </ul>
                        <PriceLine value={prices.max} suffix={suffixeDuree(prices.maxDuree)} align="center" prefix="À partir de" />
                        <Button variant={BUTTON_VARIANTS.PINK_GRADIENT} big full
                            onClick={() => goSubscribe(OFFERS.max.slug, prices.maxOffer)}>
                            Choisir {MAX_NAME}
                        </Button>
                    </section>

                    <section className="om-bp-offer">
                        <div className="om-bp-simple">Une formule plus simple&nbsp;?</div>
                        <div className="om-bp-orow">
                            <span className="om-bp-logo om-bp-logo--plus" aria-hidden="true"><i /></span>
                            <span className="om-bp-otext">
                                <span className="om-bp-oname om-bp-oname--plus">{OFFERS.plus.name}</span>
                                <span className="om-bp-oline">L'essentiel du social, débloqué.</span>
                            </span>
                        </div>
                        <ul className="om-bp-list">
                            <OfferLine label={copy.featurePlus || copy.feature} strong />
                            <OfferLine label={plusOtherArguments(triggerAction)} />
                            {PLUS_BLOCK_EXCLUSIONS.map((l) => <OfferLine key={l} label={l} included={false} />)}
                        </ul>
                        {/* Le détail de Voissa Plus se déplie sur place, comme « Voir tous les avantages
                            Premium » : l'ancien lien « Découvrir toute l'offre » emmenait vers une autre
                            vue de la modale (Rudy, 07/10). */}
                        <div className={classNames('om-bp-more', { 'is-open': showPlusDetail })}>
                            <button type="button" className="om-bp-more-toggle"
                                aria-expanded={showPlusDetail}
                                onClick={() => setShowPlusDetail(!showPlusDetail)}>
                                Voir tous les avantages {PLUS_NAME}
                                <Icon name="chevronDown" className="om-bp-more-chevron" />
                            </button>
                            {showPlusDetail && (
                                <ul className="om-bp-list om-bp-list--more">
                                    {plusMoreFeatures(triggerAction).map((l) => (
                                        <li key={l} className="om-bp-li"><Icon name="check" className="om-bp-mark" /><span>{l}</span></li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <PriceLine value={prices.plus} suffix={suffixeDuree(prices.plusDuree, { ensuite: canStartTrial })} align="center" prefix="À partir de" />
                        {/* L'essai est le seul argument gratuit de la modale : il mérite une ligne à lui,
                            pas seulement une pastille dans le coin. Elle remplace la note sous le bouton. */}
                        {canStartTrial && (
                            <div className="om-bp-trial-line">
                                <Icon name="sparkles" />
                                <span><b>Essai gratuit</b> — sans carte bancaire, rien n'est prélevé</span>
                            </div>
                        )}
                        <Button variant={BUTTON_VARIANTS.PINK_REVERSE} big full loading={submitting && canStartTrial}
                            onClick={canStartTrial ? doStartTrial : () => goSubscribe(OFFERS.plus.slug, prices.plusOffer)}>
                            {canStartTrial ? "J'active mon essai gratuit" : `Choisir ${PLUS_NAME}`}
                        </Button>
                        <TrialError />
                    </section>
                </div>

                <div className="om-bp-foot">
                    {BLOCK_REASSURANCE.map((r) => (
                        <span key={r.label} className="om-bp-reassure"><Icon name={r.icon} />{r.label}</span>
                    ))}
                </div>
                {inDrawer && <div className="om-compact-foot"><button type="button" className="om-dismiss" onClick={handleCancel}>Plus tard</button></div>}
            </>
        );

    // 1a/1b) PAGE DE CHOIX D'OFFRE (écran 02 des maquettes) — lancement, fin d'essai, et vue
    // « découvrir toute l'offre » ouverte depuis une modale de blocage.
    //
    // Deux onglets au lieu de deux cartes côte à côte : une comparaison en colonnes oblige à
    // raccourcir les listes pour qu'elles tiennent, alors qu'un onglet laisse chaque offre se
    // présenter en entier. Voissa Premium est l'onglet par défaut — c'est l'offre poussée — et
    // l'onglet Voissa Plus assume ce qu'il ne contient pas plutôt que de le taire.
    } else if (context === OFFERS_MODAL_CONTEXT.LAUNCH || context === OFFERS_MODAL_CONTEXT.TRIAL_END || isBlockedPlus || showOffers) {
        const onMax = offerTab === 'max';
        // Sur une page dédiée, l'offre se détaille : la maquette énumère tout, y compris ce que
        // Premium hérite de Voissa Plus. Un « et tout Voissa Plus inclus » en une ligne fait
        // gagner de la place, mais laisse le lecteur deviner ce qu'il achète.
        const exclusivitesMax = MAX_CARD_FEATURES.filter((f) => !f.strong).map((f) => f.label);
        // Onglet Premium : ce qui lui est PROPRE d'abord, le contenu de Voissa Plus replié
        // derrière un dépliant — treize lignes d'affilée ne se lisent pas, et la maquette les
        // sépare de la même façon. L'onglet Plus tient déjà en une liste courte.
        // Onglet Plus : même principe, les premières lignes puis un dépliant (comme la maquette),
        // pour que les deux onglets aient la même hauteur de départ.
        const lignes = onMax ? exclusivitesMax : PLUS_CARD_FEATURES.slice(0, PLUS_TAB_VISIBLE);
        // Côté Premium, sans le badge argent (Premium a le sien, gold) ni le quota de petits mots.
        const lignesRepliees = onMax
            ? PLUS_FEATURES_IN_PREMIUM
            : PLUS_CARD_FEATURES.slice(PLUS_TAB_VISIBLE);

        body = (
            <>
                <Head compact />

                <div className="om-tabs" role="tablist">
                    <button type="button" role="tab" aria-selected={onMax}
                        className={classNames('om-tab', { 'is-current': onMax })}
                        onClick={() => { setOfferTab('max'); setShowTabAll(false); }}>
                        {OFFERS.max.name}
                        <span className="om-tab-reco"><Icon name="starSharp" />Recommandé</span>
                    </button>
                    <button type="button" role="tab" aria-selected={!onMax}
                        className={classNames('om-tab', { 'is-current': !onMax })}
                        onClick={() => { setOfferTab('plus'); setShowTabAll(false); }}>
                        {OFFERS.plus.name}
                    </button>
                </div>

                <div className="om-tab-body">
                    {/* Le nom de l'offre ouverte, en titre sous la barre, comme dans la maquette. */}
                    <div className="om-tab-name">{onMax ? OFFERS.max.name : OFFERS.plus.name}</div>
                    <div className="om-tab-price">
                        <PriceLine value={onMax ? (prices.max) : (prices.plus)}
                            suffix={suffixeDuree(onMax ? prices.maxDuree : prices.plusDuree, { ensuite: !onMax && canStartTrial })}
                            align="center" prefix="À partir de" />
                    </div>
                    <div className="om-tab-lead">
                        {onMax ? "L'expérience Voissa sans aucune limite :" : "L'essentiel du social, débloqué :"}
                    </div>

                    <ul className="om-bp-list">
                        {lignes.map((l) => (
                            <li key={l} className="om-bp-li"><Icon name="check" className="om-bp-mark" /><span>{l}</span></li>
                        ))}
                    </ul>

                    {lignesRepliees.length > 0 && (
                        <div className={classNames('om-bp-more', { 'is-open': showTabAll })}>
                            <button type="button" className="om-bp-more-toggle"
                                aria-expanded={showTabAll}
                                onClick={() => setShowTabAll(!showTabAll)}>
                                {/* Même libellé que les autres dépliants (« Voir tous les avantages … »), 07/10. */}
                                Voir tous les avantages {onMax ? MAX_SHORT : PLUS_NAME}
                                <Icon name="chevronDown" className="om-bp-more-chevron" />
                            </button>
                            {showTabAll && (
                                <>
                                    {onMax && <div className="om-tab-sep">Et tout ce que contient {OFFERS.plus.name}</div>}
                                    <ul className="om-bp-list om-bp-list--more">
                                        {lignesRepliees.map((l) => (
                                            <li key={l} className="om-bp-li"><Icon name="check" className="om-bp-mark" /><span>{l}</span></li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    )}

                    {/* Ce que Voissa Plus ne contient pas, dit ici plutôt que découvert après
                        l'achat. Inutile sur l'onglet Premium : il contient tout. */}
                    {!onMax && (
                        <div className="om-tab-excl">
                            <div className="om-tab-excl-label">Non inclus dans {OFFERS.plus.name}</div>
                            <ul className="om-bp-list">
                                {exclusivitesMax.map((l) => (
                                    <li key={l} className="om-bp-li om-bp-li--no"><Icon name="xmark" className="om-bp-mark" /><span>{l}</span></li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {!onMax && canStartTrial && (
                        <div className="om-bp-trial-line">
                            <Icon name="sparkles" />
                            <span><b>Essai gratuit</b> — sans carte bancaire, rien n'est prélevé</span>
                        </div>
                    )}
                    <Button variant={BUTTON_VARIANTS.PINK_GRADIENT} big full
                        loading={submitting && !onMax && canStartTrial}
                        onClick={onMax
                            ? () => goSubscribe(OFFERS.max.slug, prices.maxOffer)
                            : (canStartTrial ? doStartTrial : () => goSubscribe(OFFERS.plus.slug, prices.plusOffer))}>
                        {!onMax && canStartTrial ? "J'active mon essai gratuit" : `Continuer avec ${onMax ? OFFERS.max.name : OFFERS.plus.name}`}
                    </Button>
                    <TrialError />
                </div>

                <div className="om-bp-foot">
                    {OFFERS_REASSURANCE.map((r) => (
                        <span key={r.label} className="om-bp-reassure"><Icon name={r.icon} />{r.label}</span>
                    ))}
                </div>
                {inDrawer && <div className="om-compact-foot"><button type="button" className="om-dismiss" onClick={handleCancel}>Plus tard</button></div>}
            </>
        );

    } else if (canBePriviliged || certifEligible) {
        // 2a) Action bloquée Max + profil ÉLIGIBLE À LA CERTIFICATION. La certif vidéo accorde
        // exactement les capabilities relationnelles qui bloquent l'action, et elle est GRATUITE :
        // on vend donc la certification (ce qu'elle débloque + CTA principal), Premium ne subsiste
        // qu'en lien secondaire. Pas de prix affiché au-dessus du CTA (il est gratuit), et pas de
        // ligne Voissa Plus : Plus n'ouvre pas le relationnel, il ne débloquerait pas l'action.
        const maxPrice = fmtPrice(prices.max);
        body = (
            <>
                {/* Trois niveaux, centrés : le sur-titre, puis le couple médaillon + titre, puis
                    le texte. Le couple a son propre conteneur — sans lui, le titre s'étire sur
                    toute la largeur et il ne reste rien à centrer. */}
                <div className="om-head om-head--certif">
                    <div className="om-eyebrow">{CERTIF_EYEBROW}</div>
                    <div className="om-head-row">
                        <div className="om-hero"><Icon name="badge" /></div>
                        <GradientHeadline headline={CERTIF_HEADLINE[triggerAction] || CERTIF_HEADLINE_DEFAULT} />
                    </div>
                    <div className="om-sub">
                        {CERTIF_SUBHEADLINE.map((l) => <span key={l}>{l}</span>)}
                    </div>
                </div>
                <div className="om-panel-wrap">
                    <div className="om-panel">
                        <div className="om-panel-label">Débloqué par la certification</div>
                        {CERTIF_PANEL.map((it, i) => {
                            const active = it.cap === triggerAction;
                            return (
                                <div key={i} className={classNames('om-pli', { 'is-active': active })}>
                                    <span className="om-pli-icon" aria-hidden="true"><Icon name={it.icon} /></span>
                                    <span className="om-pli-text">
                                        <span className={classNames({ 'om-li-strong': active })}>{it.label}</span>
                                        {it.quota && (
                                            <span className="om-pli-quota">
                                                {it.quota} · <span className="om-grad">illimité en {MAX_SHORT}</span>
                                            </span>
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="om-action">
                    <Button variant={BUTTON_VARIANTS.PINK_GRADIENT} big full onClick={goCertify}>
                        {certified ? 'Re-certifier mon profil' : 'Certifier mon profil'}
                    </Button>
                    <div className="om-action-note">{CERTIF_NOTE}</div>
                </div>

                {/* Deux voies, pas une alternative de repli : le « OU » de la maquette sépare
                    nettement la certification (gratuite, mise en avant) de l'abonnement. Sans lui,
                    Premium ressemblait à une note de bas de page sous le bouton. */}
                <div className="om-certif-or"><span>OU</span></div>

                <button type="button" className="om-certif-prem" onClick={() => goSubscribe(OFFERS.max.slug, prices.maxOffer)}>
                    <span className="om-bp-logo om-certif-prem-logo" aria-hidden="true"><i /></span>
                    <span className="om-certif-prem-text">
                        <b>Devenir {MAX_SHORT}</b>
                        <span>{MAX_NAME} débloque toutes les fonctionnalités de Voissa, sans vidéo.</span>
                        {maxPrice && <span className="om-certif-prem-price">À partir de {maxPrice} /mois</span>}
                    </span>
                    <span className="om-certif-prem-chev" aria-hidden="true"><Icon name="chevronRight" /></span>
                </button>
                <Footer />
            </>
        );
    } else {
        // 2b) Action bloquée — VOISSA PREMIUM, profil non éligible à la certification
        // (maquettes 09/2026, écran 04).
        //
        // Ici, contrairement au blocage Voissa Plus, il n'y a pas d'alternative à comparer : Plus
        // ne contient pas la fonction. La modale explique donc ce qui est bloqué, montre ce que
        // Premium ouvre, et propose l'action — le bouton porte le verbe de ce qu'on débloque, pas
        // « s'abonner » : le membre veut la fonction, l'abonnement n'est que le moyen.
        const copy = MAX_BLOCK_COPY[triggerAction] || MAX_BLOCK_FALLBACK;
        const autres = maxBlockLines(copy.feature);
        const [visibles, replies] = [autres.slice(0, 3), autres.slice(3)];

        // Vitrine : montrer ce qui est bloqué avant d'en parler. La conversation est FLOUTÉE —
        // c'est le sujet même de la modale : le message existe, il n'est pas lisible.
        const ech = MAX_SHOWCASE_SAMPLE;
        // Prénom de la conversation, sans son âge : il sert aussi au sous-titre.
        const vitrines = {
            // Bloc « Chaque jour sur Vmessenger » de Bertrand (07/10) : les volumes du chat en bulles
            // de conversation, reçues à gauche et envoyées à droite. Il remplace la conversation
            // fictive et la Constellation : Bertrand ne garde que les chiffres.
            conversation: (
                <div className="om-chat">
                    <div className="om-chat-title"><Icon name="message" />{ech.messenger.title}</div>
                    <ul className="om-chat-thread">
                        <li className="om-chat-bubble om-chat-bubble--in om-chat-bubble--hero">
                            <span className="om-chat-big om-grad">{ech.messenger.hero.value}</span>
                            <span className="om-chat-label">{ech.messenger.hero.label}</span>
                        </li>
                        {ech.messenger.media.map((m) => (
                            <li key={m.value} className={classNames('om-chat-bubble', 'om-chat-bubble--media', m.sent ? 'om-chat-bubble--out' : 'om-chat-bubble--in')}>
                                <span className="om-chat-icon" aria-hidden="true"><Icon name={m.icon} /></span>
                                <span>
                                    <span className="om-chat-num">{m.value}</span>
                                    <span className="om-chat-sub">{m.label}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                    <p className="om-chat-members">
                        <span className="om-chat-dot" aria-hidden="true" />
                        <span><b>{ech.messenger.members.value}</b> {ech.messenger.members.label}</span>
                    </p>
                </div>
            ),
            filters: (
                <div className="om-viz-chips">
                    {ech.filters.map((f) => <span key={f}><Icon name="lock" />{f}</span>)}
                </div>
            ),
            circle: (
                <div className="om-viz-circle">
                    <span className="om-viz-pill"><Icon name="lock" />{ech.circle}</span>
                    {/* Schéma repris de la maquette : cinq membres DISTINCTS, reliés au profil.
                        Des pastilles identiques et sans lien ne disaient pas qu'il s'agit d'un
                        cercle de personnes — elles faisaient décor. */}
                    <svg className="om-viz-graphic" viewBox="0 0 160 122" role="img"
                        aria-label="Cinq membres réunis autour de votre profil">
                        <defs>
                            <linearGradient id="om-circle-core" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#ff5757" />
                                <stop offset="100%" stopColor="#ff59b7" />
                            </linearGradient>
                        </defs>
                        <g stroke="#eed5e0" strokeWidth="1.2" opacity=".6">
                            <line x1="80" y1="61" x2="80" y2="22" />
                            <line x1="80" y1="61" x2="24" y2="45" />
                            <line x1="80" y1="61" x2="136" y2="45" />
                            <line x1="80" y1="61" x2="42" y2="102" />
                            <line x1="80" y1="61" x2="118" y2="102" />
                        </g>
                        <g stroke="#fff" strokeWidth="2.6">
                            <circle cx="80" cy="22" r="12" fill="#f7906e" />
                            <circle cx="24" cy="45" r="11" fill="#f5b45e" />
                            <circle cx="136" cy="45" r="11" fill="#8b7cf0" />
                            <circle cx="42" cy="102" r="11" fill="#74beea" />
                            <circle cx="118" cy="102" r="11" fill="#f27ba9" />
                        </g>
                        <circle cx="80" cy="61" r="21" fill="url(#om-circle-core)" stroke="#fff" strokeWidth="3" />
                    </svg>
                </div>
            ),
            incognito: (
                <div className="om-viz-symbol" aria-hidden="true">
                    <Icon name="incognito" />
                </div>
            ),
        };
        const showcase = vitrines[MAX_SHOWCASE[triggerAction]] || null;
        // Le bloc du chat se pose sur le fond de la carte, sans la bande teintée des autres vitrines.
        const showcaseChat = MAX_SHOWCASE[triggerAction] === 'conversation';

        body = (
            <>
                <div className="om-mb-head">
                    <div className="om-mb-eyebrow">Réservé aux membres {MAX_SHORT}</div>
                    <div className="om-mb-title">
                        {copy.headline[0]}<br /><span className="om-grad">{copy.headline[1]}</span>
                    </div>
                    {copy.lead && <p className="om-mb-lead">{copy.lead}</p>}
                </div>

                {showcase && <div className={classNames('om-mb-viz', { 'om-mb-viz--chat': showcaseChat })}>{showcase}</div>}

                <div className="om-mb-offer">
                    <div className="om-bp-orow">
                        <span className="om-bp-logo" aria-hidden="true"><i /></span>
                        <span className="om-bp-otext">
                            <span className="om-bp-oname">{OFFERS.max.name}</span>
                            <span className="om-bp-oline">Tout Voissa, sans limites.</span>
                        </span>
                        {/* Pas de « Recommandé » quand Premium est la seule offre affichée : il n'y a
                            rien à comparer (Bertrand et Vincent, 19/10). */}
                    </div>

                    <ul className="om-bp-list">
                        <li className="om-bp-li om-bp-li--key">
                            <Icon name="check" className="om-bp-mark" />
                            <span>
                                {copy.feature}
                                {copy.featureNote && <em className="om-mb-note">{copy.featureNote}</em>}
                            </span>
                        </li>
                        {visibles.map((l) => (
                            <li key={l} className="om-bp-li"><Icon name="check" className="om-bp-mark" /><span>{l}</span></li>
                        ))}
                    </ul>

                    {!!replies.length && (
                        <div className={classNames('om-bp-more', { 'is-open': showMaxDetail })}>
                            <button type="button" className="om-bp-more-toggle"
                                aria-expanded={showMaxDetail}
                                onClick={() => setShowMaxDetail(!showMaxDetail)}>
                                Voir tous les avantages {MAX_SHORT}
                                <Icon name="chevronDown" className="om-bp-more-chevron" />
                            </button>
                            {showMaxDetail && (
                                <ul className="om-bp-list om-bp-list--more">
                                    {replies.map((l) => (
                                        <li key={l} className="om-bp-li"><Icon name="check" className="om-bp-mark" /><span>{l}</span></li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    <PriceLine value={prices.max} suffix={suffixeDuree(prices.maxDuree)} align="center" prefix="À partir de" />
                    <Button variant={BUTTON_VARIANTS.PINK_GRADIENT} big full
                        onClick={() => goSubscribe(OFFERS.max.slug, prices.maxOffer)}>
                        {copy.cta}
                    </Button>
                </div>

                <div className="om-bp-foot">
                    {BLOCK_REASSURANCE.map((r) => (
                        <span key={r.label} className="om-bp-reassure"><Icon name={r.icon} />{r.label}</span>
                    ))}
                </div>
                {inDrawer && <div className="om-compact-foot"><button type="button" className="om-dismiss" onClick={handleCancel}>Plus tard</button></div>}
            </>
        );
    }

    // `wide` (max-width 100%) : auto pour tous les gabarits à comparatif (lancement, fin d'essai,
    // bloqué Plus, vue comparatif), ou forcé via `fullWidth` pour les gabarits mono-colonne qu'on
    // veut voir occuper toute la largeur du drawer (ex. CTA des statistiques).
    const blockedMaxLayout = context === OFFERS_MODAL_CONTEXT.BLOCKED && tier === 'max'
        && !showOffers && !(canBePriviliged || certifEligible);

    const wide = !blockedPlusLayout && !blockedMaxLayout
        && (fullWidth || showOffers || context === OFFERS_MODAL_CONTEXT.LAUNCH || context === OFFERS_MODAL_CONTEXT.TRIAL_END || isBlockedPlus);

    return (
        <div className={classNames('om', { 'om--wide': wide, 'om--bp': blockedPlusLayout || blockedMaxLayout, 'om--drawer': inDrawer })}>
            {closeBtn}
            {body}
        </div>
    );
};

export default OffersModal;
